import { Controller } from "@hotwired/stimulus"

// Drives the creator-page family matrix that lives INSIDE the mode turbo-frame:
//  1. the horizontal-scroll edge-fade affordance (flag no-overflow / scroll-end
//     so the right-edge gradient only shows while columns remain off-screen), and
//  2. the chunked "Load more entries" reveal (deferred rows live inert in
//     <template>s and clone into the grid a batch at a time).
//
// It sits inside the frame on purpose: a mode switch swaps the frame body, so
// Stimulus disconnects the old instance and connects a fresh one — no manual
// re-init, and the load-more cursor/templates reset for the new mode for free.
const BATCH = 8

export default class extends Controller {
  private scrollEl: HTMLElement | null = null
  private anchor: HTMLElement | null = null
  private btn: HTMLButtonElement | null = null
  private templates: HTMLTemplateElement[] = []
  private cursor = 0
  private readonly boundSync = () => this.syncScroll()
  private readonly boundLoadMore = (event: Event) => this.loadMore(event)

  connect(): void {
    this.scrollEl = this.element.querySelector<HTMLElement>("[data-fam-scroll]")
    this.anchor = this.element.querySelector<HTMLElement>("[data-fam-insert-anchor]")
    this.btn = this.element.querySelector<HTMLButtonElement>("[data-fam-loadmore]")
    this.templates = Array.from(this.element.querySelectorAll<HTMLTemplateElement>("template[data-fam-row]"))
    this.cursor = 0

    if (this.scrollEl) {
      this.scrollEl.addEventListener("scroll", this.boundSync, { passive: true })
      window.addEventListener("resize", this.boundSync)
      this.syncScroll()
    }
    if (this.btn) this.btn.addEventListener("click", this.boundLoadMore)
  }

  disconnect(): void {
    if (this.scrollEl) this.scrollEl.removeEventListener("scroll", this.boundSync)
    window.removeEventListener("resize", this.boundSync)
    if (this.btn) this.btn.removeEventListener("click", this.boundLoadMore)
  }

  // Flag whether the matrix overflows and whether we're scrolled to the end, so
  // the CSS right-edge fade only renders while more columns remain off-screen.
  private syncScroll(): void {
    const wrap = this.scrollEl
    if (!wrap) return
    const overflow = wrap.scrollWidth - wrap.clientWidth
    if (overflow <= 1) {
      wrap.setAttribute("data-no-overflow", "")
      wrap.removeAttribute("data-scroll-end")
      return
    }
    wrap.removeAttribute("data-no-overflow")
    if (wrap.scrollLeft >= overflow - 1) wrap.setAttribute("data-scroll-end", "")
    else wrap.removeAttribute("data-scroll-end")
  }

  // Clone the next batch of deferred rows into the grid, just before the insert
  // anchor so grid order is preserved. Defers the clone a frame so the spinner
  // paints first, then re-wires stack-group / lightbox on the new cells.
  private loadMore(event: Event): void {
    event.preventDefault()
    const btn = this.btn
    if (!btn || !this.anchor) return

    const label = btn.querySelector<HTMLElement>("[data-fam-loadmore-label]")
    const spinner = btn.querySelector<HTMLElement>("[data-fam-loadmore-spinner]")
    const counter = btn.querySelector<HTMLElement>("[data-fam-loadmore-count]")
    const wrapEl = this.element.querySelector<HTMLElement>("[data-fam-loadmore-wrap]")

    label?.classList.add("hidden")
    spinner?.classList.remove("hidden")
    btn.disabled = true

    requestAnimationFrame(() => {
      const slice = this.templates.slice(this.cursor, this.cursor + BATCH)
      for (const tpl of slice) {
        this.anchor!.parentNode!.insertBefore(tpl.content.cloneNode(true), this.anchor)
      }
      this.cursor += slice.length

      // arena dispatches this so stack-group / lightbox rebind on injected cells.
      document.dispatchEvent(new CustomEvent("arena:content-updated"))
      this.syncScroll()

      const remaining = this.templates.length - this.cursor
      if (remaining <= 0) {
        wrapEl?.remove()
        return
      }
      if (counter) counter.textContent = String(remaining)
      label?.classList.remove("hidden")
      spinner?.classList.add("hidden")
      btn.disabled = false
    })
  }
}
